/**
 * communityIdentity — shared helpers for resolving a community's visual
 * identity (logo / hero, country flag, ancestry line) and the lookup maps
 * the walk needs.
 *
 * Extracted verbatim from MyCommunitiesCardsPage so the Communities Rail and
 * the cards page share one implementation instead of forking the flag/hero
 * logic (ANALYSIS_LEFT_RAILS.md flags this duplication as debt). GUID keys
 * are upper-cased everywhere per the CLAUDE.md case-insensitive GUID rule.
 */
import { getCommunityOverride } from '../../Data/CommunityConfig/communityConfigOverrides';
import { communityConfigStore } from '../../Data/store';
import { mediaSrc } from '../../api/mediaUrl';

export type CommunityEntity = {
  entity_guid: string;
  name?: string;
  entity_type?: string;
  is_active?: boolean;
  visibility?: string;
  code?: string;
  /** Slug segment for the canonical slug URL (DEV-947). */
  slug?: string | null;
  hero_image_url?: string;
  /**
   * DEV-961 / ADR-006. When set, this community is the user's auto-
   * provisioned private workshop. Workshops are excluded from the
   * user-facing Communities navigation per the agreed-upon design.
   */
  personal_owner_user_guid?: string | null;
};

export type CommunityRelationship = {
  parent_entity_guid: string;
  child_entity_guid: string;
};

/** Convert ISO 3166-1 alpha-2 code to flag emoji. */
export function countryFlag(code: string | null | undefined): string {
  if (!code || code.length < 2) return '';
  const upper = code.toUpperCase().trim();
  const cc = upper.indexOf('-') === 2 ? upper.slice(0, 2) : (upper.length === 2 ? upper : '');
  if (!cc || !/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...Array.from(cc).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Walk a community's ancestry to the nearest geo_country and return its flag. */
export function findCountryFlag(
  entity: CommunityEntity,
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
): string {
  const code = entity.code;
  const etype = (entity.entity_type || '').toLowerCase();
  if (etype === 'geo_country' && code) return countryFlag(code);
  let guid = parentMap.get(entity.entity_guid.toUpperCase());
  for (let i = 0; i < 5 && guid; i++) {
    const parent = entityMap.get(guid);
    if (!parent) break;
    const ptype = (parent.entity_type || '').toLowerCase();
    if (ptype === 'geo_country' && parent.code) return countryFlag(parent.code);
    guid = parentMap.get(guid);
  }
  return '';
}

/** Comma-joined ancestor names (excluding the "Earth" root). */
export function resolveAncestryLine(
  entity: CommunityEntity,
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
): string {
  const names: string[] = [];
  let guid = parentMap.get(entity.entity_guid.toUpperCase());
  const seen = new Set<string>();
  for (let i = 0; i < 6 && guid && !seen.has(guid); i++) {
    seen.add(guid);
    const ancestor = entityMap.get(guid);
    if (!ancestor) break;
    const name = ancestor.name || '';
    if (name.toLowerCase() !== 'earth' && name.trim()) {
      names.push(name);
    }
    guid = parentMap.get(guid);
  }
  return names.join(', ');
}

/** Resolve hero image URL: override → config store → entity prop. */
export function resolveHeroUrl(guid: string, entity?: CommunityEntity): string | null {
  try {
    const ov = getCommunityOverride(guid) as any;
    if (ov?.hero_image_url) return mediaSrc(ov.hero_image_url);
  } catch { /* ignore */ }
  const storeEntry = communityConfigStore.get(String(guid).toUpperCase());
  if (storeEntry?.hero_image_url) return mediaSrc(storeEntry.hero_image_url);
  return mediaSrc(entity?.hero_image_url || null);
}

/** First non-whitespace character of a community name, for avatar fallback. */
export function communityInitial(name?: string): string {
  const trimmed = (name || '').trim();
  return trimmed ? trimmed[0].toUpperCase() : '•';
}

/** Build upper-cased entity_guid → entity lookup. */
export function buildEntityMap(
  entities: CommunityEntity[] | undefined,
): Map<string, CommunityEntity> {
  const m = new Map<string, CommunityEntity>();
  for (const e of entities || []) {
    m.set(e.entity_guid.toUpperCase(), e);
  }
  return m;
}

/** Build upper-cased child → parent lookup from relationships. */
export function buildParentMap(
  relationships: CommunityRelationship[] | undefined,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of relationships || []) {
    m.set(r.child_entity_guid.toUpperCase(), r.parent_entity_guid.toUpperCase());
  }
  return m;
}
